// ─── ConfirmDialog.jsx ────────────────────────────────────────────────────────
// Modal confirmation for destructive actions.
// Import as: import ConfirmDialog from "./ConfirmDialog";
//
// Usage:
//   const [confirm, setConfirm] = useState(null);
//
//   // Trigger:
//   setConfirm({
//     title: "Close this case?",
//     body: "This marks the case as closed.",
//     confirmLabel: "Close Case",
//     onConfirm: async () => { await doClose(); setConfirm(null); },
//   });
//
//   // In JSX:
//   <ConfirmDialog open={!!confirm} {...confirm} onCancel={() => setConfirm(null)} />

import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";

export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel   = null,
  confirmColor   = null,
  onConfirm,
  onCancel,
  loading        = false,
}) {
  const C = useTheme();
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('confirmDialog.confirm');
  const resolvedColor = confirmColor ?? C.danger;
  if (!open) return null;

  return (
    <div
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999, fontFamily: "'DM Mono', monospace" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 12, padding: "28px 30px", maxWidth: 420, width: "90%",
          boxShadow: "0 16px 64px rgba(0,0,0,0.6)" }}
      >
        <div style={{ color: C.text, fontSize: 17, fontWeight: 700,
          fontFamily: "'Syne', sans-serif", marginBottom: 10 }}>
          {title}
        </div>
        {body && (
          <div style={{ color: C.textMid, fontSize: 13, lineHeight: 1.65,
            marginBottom: 26 }}>
            {body}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{ background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: 7, color: C.textMid, padding: "8px 20px",
              cursor: "pointer", fontSize: 13 }}
          >
            {t('confirmDialog.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{ background: loading ? C.border : resolvedColor, border: "none",
              borderRadius: 7, color: "#fff", padding: "8px 20px", cursor: loading ? "wait" : "pointer",
              fontSize: 13, fontWeight: 600, opacity: loading ? 0.7 : 1 }}
          >
            {loading ? t('auth.pleaseWait') : resolvedConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}